import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAvatar, type Mood } from '../Assets/Avatars';
import { useAvatar } from '../context/AvatarContext';
import DPad, { type Direction } from './DPad';

export interface NavTarget {
  id: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  route: string;
  locked?: boolean;
  lockedHint?: string;
}

interface Props {
  width: number;
  height: number;
  targets: NavTarget[];
  onEnter: (target: NavTarget) => void;
  initialPosition?: { x: number; y: number };
  /** Override the mood derived from movement (e.g. 'happy' on achievement). */
  moodOverride?: Mood;
  /** Called when focused target changes (e.g. for analytics or label tooltip). */
  onFocusChange?: (id: string | null) => void;
  /**
   * Pause input handling without unmounting. Used by the modal stack: when a
   * RealmModal opens, the world-map canvas stays in the tree (preserving
   * avatar position) but stops responding to keys so the modal's own canvas
   * owns the arrow keys.
   */
  enabled?: boolean;
}

const SPEED = 320; // logical units per second
const AVATAR_SIZE = 64;

function isCoarsePointer(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return (el as HTMLElement).isContentEditable === true;
}

const IslandCanvas: React.FC<Props> = ({
  width,
  height,
  targets,
  onEnter,
  initialPosition,
  moodOverride,
  onFocusChange,
  enabled = true,
}) => {
  const { character } = useAvatar();

  // Use refs for per-frame state so the rAF loop doesn't trigger React renders.
  const posRef = useRef({
    x: initialPosition?.x ?? width / 2,
    y: initialPosition?.y ?? height / 2,
  });
  const velRef = useRef({ x: 0, y: 0 });
  const facingRef = useRef<'left' | 'right'>('right');
  const heldRef = useRef<Record<Direction, boolean>>({ up: false, down: false, left: false, right: false });
  const focusedRef = useRef<NavTarget | null>(null);
  const targetsRef = useRef(targets);
  const onEnterRef = useRef(onEnter);
  const onFocusChangeRef = useRef(onFocusChange);

  const mascotElRef = useRef<HTMLImageElement | null>(null);
  const containerElRef = useRef<HTMLDivElement | null>(null);

  const [moving, setMoving] = useState(false);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [showDPad, setShowDPad] = useState(isCoarsePointer());

  useEffect(() => { targetsRef.current = targets; }, [targets]);
  useEffect(() => { onEnterRef.current = onEnter; }, [onEnter]);
  useEffect(() => { onFocusChangeRef.current = onFocusChange; }, [onFocusChange]);

  // Show DPad on first touch even on hybrid devices.
  useEffect(() => {
    const onFirstTouch = () => setShowDPad(true);
    window.addEventListener('touchstart', onFirstTouch, { once: true, passive: true });
    return () => window.removeEventListener('touchstart', onFirstTouch);
  }, []);

  const recomputeVelocity = useCallback(() => {
    const h = heldRef.current;
    let vx = 0;
    let vy = 0;
    if (h.left) vx -= 1;
    if (h.right) vx += 1;
    if (h.up) vy -= 1;
    if (h.down) vy += 1;
    // normalize so diagonals aren't faster than straight lines
    const mag = Math.hypot(vx, vy);
    if (mag > 0) {
      vx = (vx / mag) * SPEED;
      vy = (vy / mag) * SPEED;
      if (vx < 0) facingRef.current = 'left';
      else if (vx > 0) facingRef.current = 'right';
    }
    // The avatar SVGs are drawn facing left by default, so we mirror when facing right.
    velRef.current = { x: vx, y: vy };
    setMoving(mag > 0);
  }, []);

  const press = useCallback((dir: Direction) => {
    heldRef.current[dir] = true;
    recomputeVelocity();
  }, [recomputeVelocity]);

  const release = useCallback((dir: Direction) => {
    heldRef.current[dir] = false;
    recomputeVelocity();
  }, [recomputeVelocity]);

  const confirm = useCallback(() => {
    const t = focusedRef.current;
    if (!t) return;
    onEnterRef.current(t);
  }, []);

  // Keyboard listeners scoped to window — only while mounted *and* enabled.
  // When a modal opens on top, the parent flips `enabled` to false so the
  // background canvas stops fighting the modal for arrow-key focus.
  useEffect(() => {
    if (!enabled) {
      // Drop any keys that were held when we got disabled so the avatar
      // doesn't keep drifting silently behind a modal.
      heldRef.current = { up: false, down: false, left: false, right: false };
      recomputeVelocity();
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(document.activeElement)) return;
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          press('up');
          e.preventDefault();
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          press('down');
          e.preventDefault();
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          press('left');
          e.preventDefault();
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          press('right');
          e.preventDefault();
          break;
        case 'Enter':
        case ' ':
          confirm();
          e.preventDefault();
          break;
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          release('up'); break;
        case 'ArrowDown':
        case 's':
        case 'S':
          release('down'); break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          release('left'); break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          release('right'); break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [press, release, confirm, recomputeVelocity, enabled]);

  // Animation loop.
  useEffect(() => {
    let rafId = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000); // cap at 50ms to avoid jumps when tab refocuses
      last = now;

      const v = velRef.current;
      if (v.x !== 0 || v.y !== 0) {
        const next = {
          x: posRef.current.x + v.x * dt,
          y: posRef.current.y + v.y * dt,
        };
        const half = AVATAR_SIZE / 2;
        next.x = Math.max(half, Math.min(width - half, next.x));
        next.y = Math.max(half, Math.min(height - half, next.y));
        posRef.current = next;

        const el = mascotElRef.current;
        if (el) {
          const flip = facingRef.current === 'right' ? -1 : 1;
          el.style.transform = `translate(${next.x - half}px, ${next.y - half}px) scaleX(${flip})`;
        }
      }

      // collision check — first target whose box contains mascot center
      const cx = posRef.current.x;
      const cy = posRef.current.y;
      let hit: NavTarget | null = null;
      for (const t of targetsRef.current) {
        if (cx >= t.x && cx <= t.x + t.w && cy >= t.y && cy <= t.y + t.h) {
          hit = t;
          break;
        }
      }
      if (hit?.id !== focusedRef.current?.id) {
        focusedRef.current = hit;
        setFocusedId(hit?.id ?? null);
        onFocusChangeRef.current?.(hit?.id ?? null);
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [width, height]);

  // Apply initial position once on mount.
  useEffect(() => {
    const el = mascotElRef.current;
    if (!el) return;
    const half = AVATAR_SIZE / 2;
    el.style.transform = `translate(${posRef.current.x - half}px, ${posRef.current.y - half}px)`;
  }, []);

  // Auto-scroll the parent container so mascot stays in view.
  useEffect(() => {
    if (!moving) return;
    const id = window.setInterval(() => {
      const container = containerElRef.current?.parentElement;
      if (!container) return;
      const margin = 80;
      const left = posRef.current.x - container.clientWidth / 2;
      const top = posRef.current.y - container.clientHeight / 2;
      const visibleLeft = container.scrollLeft;
      const visibleRight = visibleLeft + container.clientWidth;
      const visibleTop = container.scrollTop;
      const visibleBottom = visibleTop + container.clientHeight;
      if (posRef.current.x < visibleLeft + margin || posRef.current.x > visibleRight - margin) {
        container.scrollLeft = Math.max(0, left);
      }
      if (posRef.current.y < visibleTop + margin || posRef.current.y > visibleBottom - margin) {
        container.scrollTop = Math.max(0, top);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [moving]);

  if (!character) return null;

  const mood: Mood = moodOverride ?? (moving ? 'moving' : 'idle');
  const avatarSrc = getAvatar(character, mood);
  const initialFlip = facingRef.current === 'right' ? -1 : 1;
  const initialHalf = AVATAR_SIZE / 2;

  return (
    <>
      <div
        ref={containerElRef}
        role="application"
        aria-label="island map — use arrow keys or the on-screen pad to move, enter to choose a realm"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      >
        {/* Highlight halos for focused target */}
        {targets.map(t => {
          const isFocused = t.id === focusedId;
          if (!isFocused) return null;
          return (
            <div
              key={`halo-${t.id}`}
              style={{
                position: 'absolute',
                left: t.x - 6,
                top: t.y - 6,
                width: t.w + 12,
                height: t.h + 12,
                border: '2.4px solid var(--w-amber)',
                borderRadius: 16,
                boxShadow: '0 0 0 4px rgba(232,180,80,0.25)',
                pointerEvents: 'none',
                animation: 'island-halo 1.4s ease-in-out infinite',
              }}
            />
          );
        })}

        {/* Mascot */}
        <img
          ref={mascotElRef}
          src={avatarSrc}
          alt={`${character} avatar`}
          width={AVATAR_SIZE}
          height={AVATAR_SIZE}
          draggable={false}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            transform: `translate(${posRef.current.x - initialHalf}px, ${posRef.current.y - initialHalf}px) scaleX(${initialFlip})`,
            transformOrigin: 'center center',
            transition: 'none',
            pointerEvents: 'none',
            filter: 'drop-shadow(2px 3px 0 rgba(30,27,22,0.35))',
            zIndex: 4,
          }}
        />

        {/* Focused-realm label tooltip */}
        {focusedId && (() => {
          const t = targets.find(x => x.id === focusedId);
          if (!t) return null;
          return (
            <div
              style={{
                position: 'absolute',
                left: t.x + t.w / 2,
                top: t.y - 26,
                transform: 'translateX(-50%)',
                padding: '3px 8px',
                background: 'var(--ink)',
                color: 'var(--paper)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10,
                borderRadius: 4,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 6,
              }}
            >
              {t.locked ? `🔒 ${t.label}` : `${t.label} · press enter`}
            </div>
          );
        })()}
      </div>

      {/* DPad lives outside the scaled canvas so it stays on screen */}
      {showDPad && (
        <DPadLayer onPress={press} onRelease={release} onConfirm={confirm} />
      )}

      {/* Halo keyframes */}
      <style>{`
        @keyframes island-halo {
          0%, 100% { box-shadow: 0 0 0 4px rgba(232,180,80,0.25); }
          50%      { box-shadow: 0 0 0 8px rgba(232,180,80,0.10); }
        }
      `}</style>
    </>
  );
};

const DPadLayer: React.FC<{
  onPress: (d: Direction) => void;
  onRelease: (d: Direction) => void;
  onConfirm: () => void;
}> = ({ onPress, onRelease, onConfirm }) => (
  <div
    style={{
      position: 'fixed',
      right: 16,
      bottom: 16,
      zIndex: 50,
      pointerEvents: 'auto',
    }}
  >
    <DPad onPress={onPress} onRelease={onRelease} onConfirm={onConfirm} />
  </div>
);

export default IslandCanvas;
